import { useEffect, useCallback } from 'react';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase';

export const useAnalytics = () => {
  const trackEvent = useCallback((eventName, parameters = {}) => {
    if (analytics) {
      logEvent(analytics, eventName, parameters);
    }
  }, []);

  const trackPageView = useCallback((pageName) => {
    trackEvent('page_view', {
      page_name: pageName,
      page_location: window.location.href,
      page_title: document.title
    });
  }, [trackEvent]);

  const trackButtonClick = useCallback((buttonName, additionalParams = {}) => {
    trackEvent('button_click', {
      button_name: buttonName,
      ...additionalParams
    });
  }, [trackEvent]);

  const trackSimulationEvent = useCallback((eventType, simulationData = {}) => {
    trackEvent('simulation_event', {
      event_type: eventType,
      ...simulationData
    });
  }, [trackEvent]);

  const trackError = useCallback((errorMessage, errorCode = null) => {
    trackEvent('error', {
      error_message: errorMessage,
      error_code: errorCode
    });
  }, [trackEvent]);

  // ===== EVENTOS ESPECÍFICOS PARA SIMULARCP =====

  // Eventos de Simulación Principal
  const trackSimulationStart = useCallback((simulationType, additionalData = {}) => {
    trackSimulationEvent('simulation_started', {
      simulation_type: simulationType, // 'advanced', 'basic', 'ovace', 'basic-ovace'
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackSimulationEvent]);

  const trackSimulationStop = useCallback((simulationType, duration, additionalData = {}) => {
    trackSimulationEvent('simulation_stopped', {
      simulation_type: simulationType,
      duration_seconds: duration,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackSimulationEvent]);

  const trackSimulationComplete = useCallback((simulationType, duration, success, additionalData = {}) => {
    trackSimulationEvent('simulation_completed', {
      simulation_type: simulationType,
      duration_seconds: duration,
      success: success,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackSimulationEvent]);

  // Eventos de Caso
  const trackCaseStart = useCallback((additionalData = {}) => {
    trackEvent('case_started', {
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  const trackCaseStop = useCallback((duration, additionalData = {}) => {
    trackEvent('case_stopped', {
      duration_seconds: duration,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  // Eventos de PCR (Parada Cardiorrespiratoria)
  const trackPCRStart = useCallback((additionalData = {}) => {
    trackEvent('pcr_started', {
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  const trackPCRStop = useCallback((duration, additionalData = {}) => {
    trackEvent('pcr_stopped', {
      duration_seconds: duration,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  const trackROSC = useCallback((duration, additionalData = {}) => {
    trackEvent('rosc_achieved', {
      duration_seconds: duration,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  // Eventos de Intervenciones Médicas
  const trackCompressions = useCallback((action, count = null, additionalData = {}) => {
    trackEvent('compressions_action', {
      action: action, // 'start', 'stop', 'count'
      compressions_count: count,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  const trackVentilations = useCallback((action, count = null, additionalData = {}) => {
    trackEvent('ventilations_action', {
      action: action, // 'start', 'stop', 'count'
      ventilations_count: count,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  const trackDefibrillation = useCallback((shockNumber, additionalData = {}) => {
    trackEvent('defibrillation_shock', {
      shock_number: shockNumber,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  const trackMedication = useCallback((medicationType, additionalData = {}) => {
    trackEvent('medication_administered', {
      medication_type: medicationType, // 'adrenaline', 'amiodarone', 'naloxone', etc.
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  // Eventos de Evaluación
  const trackRhythmAssessment = useCallback((rhythmType, additionalData = {}) => {
    trackEvent('rhythm_assessment', {
      rhythm_type: rhythmType, // 'asystole', 'vf', 'vt', 'pea', etc.
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  const trackCausaParada = useCallback((causaH, causaT, additionalData = {}) => {
    trackEvent('causa_parada_selected', {
      causa_h: causaH,
      causa_t: causaT,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  // Eventos de Diálogos
  const trackDialogOpen = useCallback((dialogType, additionalData = {}) => {
    trackEvent('dialog_opened', {
      dialog_type: dialogType, // 'ritmo_paciente', 'causa_parada', 'tecnicas_farmacos', 'notas', etc.
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  const trackDialogClose = useCallback((dialogType, action, additionalData = {}) => {
    trackEvent('dialog_closed', {
      dialog_type: dialogType,
      action: action, // 'confirm', 'cancel', 'save'
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  // Eventos de Calidad RCP
  const trackRCPQuality = useCallback((qualityData, additionalData = {}) => {
    trackEvent('rcp_quality_assessment', {
      quality_data: qualityData,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  // Eventos de Habilidades No Técnicas
  const trackNonTechnicalSkills = useCallback((skillsData, additionalData = {}) => {
    trackEvent('non_technical_skills_assessment', {
      skills_data: skillsData,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  // Eventos de Reporte
  const trackReportGenerated = useCallback((reportType, additionalData = {}) => {
    trackEvent('report_generated', {
      report_type: reportType, // 'pdf', '24h', 'save'
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  const trackReportSaved = useCallback((reportName, additionalData = {}) => {
    trackEvent('report_saved', {
      report_name: reportName,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  // Eventos de Navegación
  const trackNavigation = useCallback((fromPage, toPage, additionalData = {}) => {
    trackEvent('navigation', {
      from_page: fromPage,
      to_page: toPage,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  // Eventos de Configuración
  const trackSettingsChange = useCallback((settingType, newValue, additionalData = {}) => {
    trackEvent('settings_changed', {
      setting_type: settingType, // 'language', 'theme', 'notifications'
      new_value: newValue,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  // Eventos de Acceso Vascular
  const trackVascularAccess = useCallback((accessType, additionalData = {}) => {
    trackEvent('vascular_access', {
      access_type: accessType, // 'peripheral', 'central', 'none'
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  // Eventos de Vía Aérea
  const trackAirwayManagement = useCallback((airwayType, additionalData = {}) => {
    trackEvent('airway_management', {
      airway_type: airwayType, // 'eti', 'lma', 'basic', 'none'
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  // Eventos de Técnicas y Fármacos
  const trackTechniqueUsed = useCallback((techniqueType, additionalData = {}) => {
    trackEvent('technique_used', {
      technique_type: techniqueType,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  const trackDrugAdministered = useCallback((drugType, additionalData = {}) => {
    trackEvent('drug_administered', {
      drug_type: drugType,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  // Eventos de OVACE (Obstrucción de Vía Aérea por Cuerpo Extraño)
  const trackOVACEAction = useCallback((actionType, additionalData = {}) => {
    trackEvent('ovace_action', {
      action_type: actionType, // 'back_blows', 'abdominal_thrusts', 'chest_compressions'
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  // Eventos de Soporte Vital Básico
  const trackBasicLifeSupport = useCallback((actionType, additionalData = {}) => {
    trackEvent('basic_life_support', {
      action_type: actionType, // 'consciousness_check', 'airway_open', 'breathing_check', 'aed_attached'
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }, [trackEvent]);

  // ===== EVENTOS DE FEEDBACK =====
  
  // Evento principal de feedback enviado
  const trackFeedbackSubmitted = useCallback((feedbackData, additionalData = {}) => {
    trackEvent('feedback_submitted', {
      // Ratings (1-5 estrellas)
      rating_intuitive: feedbackData.intuitive || 0,
      rating_agility: feedbackData.agility || 0,
      rating_usefulness: feedbackData.usefulness || 0,
      
      // Recomendación (true/false)
      would_recommend: feedbackData.recommendation,
      
      // Comentarios (solo longitud, no contenido por privacidad)
      has_comments: !!feedbackData.comments,
      comments_length: feedbackData.comments ? feedbackData.comments.length : 0,
      
      // Idioma del usuario
      user_language: additionalData.language || 'unknown',
      
      // Información de sesión
      session_id: additionalData.sessionId || 'unknown',
      timestamp: new Date().toISOString(),
      
      // Información del dispositivo
      user_agent: navigator.userAgent,
      screen_resolution: `${screen.width}x${screen.height}`,
      
      ...additionalData
    });
  }, [trackEvent]);

  // Evento de inicio de feedback (cuando el usuario accede a la página)
  const trackFeedbackStarted = useCallback((additionalData = {}) => {
    trackEvent('feedback_started', {
      timestamp: new Date().toISOString(),
      user_language: additionalData.language || 'unknown',
      ...additionalData
    });
  }, [trackEvent]);

  // Evento de rating individual (para tracking detallado)
  const trackFeedbackRating = useCallback((category, rating, additionalData = {}) => {
    trackEvent('feedback_rating_given', {
      rating_category: category, // 'intuitive', 'agility', 'usefulness'
      rating_value: rating, // 1-5
      timestamp: new Date().toISOString(),
      user_language: additionalData.language || 'unknown',
      ...additionalData
    });
  }, [trackEvent]);

  // Evento de recomendación seleccionada
  const trackFeedbackRecommendation = useCallback((recommendation, additionalData = {}) => {
    trackEvent('feedback_recommendation_selected', {
      recommendation: recommendation, // true/false
      timestamp: new Date().toISOString(),
      user_language: additionalData.language || 'unknown',
      ...additionalData
    });
  }, [trackEvent]);

  // Evento de comentarios escritos (sin contenido, solo metadata)
  const trackFeedbackComments = useCallback((hasComments, length, additionalData = {}) => {
    trackEvent('feedback_comments_written', {
      has_comments: hasComments,
      comments_length: length,
      timestamp: new Date().toISOString(),
      user_language: additionalData.language || 'unknown',
      ...additionalData
    });
  }, [trackEvent]);

  // Evento de error en feedback
  const trackFeedbackError = useCallback((errorType, errorMessage, additionalData = {}) => {
    trackEvent('feedback_error', {
      error_type: errorType, // 'submission_error', 'validation_error', 'network_error'
      error_message: errorMessage,
      timestamp: new Date().toISOString(),
      user_language: additionalData.language || 'unknown',
      ...additionalData
    });
  }, [trackEvent]);

  // Evento de cancelación de feedback
  const trackFeedbackCancelled = useCallback((additionalData = {}) => {
    trackEvent('feedback_cancelled', {
      timestamp: new Date().toISOString(),
      user_language: additionalData.language || 'unknown',
      ...additionalData
    });
  }, [trackEvent]);

  return {
    // Métodos básicos
    trackEvent,
    trackPageView,
    trackButtonClick,
    trackSimulationEvent,
    trackError,
    
    // Eventos específicos de SimulaRCP
    trackSimulationStart,
    trackSimulationStop,
    trackSimulationComplete,
    trackCaseStart,
    trackCaseStop,
    trackPCRStart,
    trackPCRStop,
    trackROSC,
    trackCompressions,
    trackVentilations,
    trackDefibrillation,
    trackMedication,
    trackRhythmAssessment,
    trackCausaParada,
    trackDialogOpen,
    trackDialogClose,
    trackRCPQuality,
    trackNonTechnicalSkills,
    trackReportGenerated,
    trackReportSaved,
    trackNavigation,
    trackSettingsChange,
    trackVascularAccess,
    trackAirwayManagement,
    trackTechniqueUsed,
    trackDrugAdministered,
    trackOVACEAction,
    trackBasicLifeSupport,
    
    // Eventos de Feedback
    trackFeedbackSubmitted,
    trackFeedbackStarted,
    trackFeedbackRating,
    trackFeedbackRecommendation,
    trackFeedbackComments,
    trackFeedbackError,
    trackFeedbackCancelled
  };
}; 